import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import Typography from '@/components/base/Typography'
import { useLenis } from '@/contexts/lenis'
import { MediaQuery } from '@/constants'

import { largeQuoteRevealAnimation } from './animations'
import { cn } from '@/lib/utils'

export type LargeQuoteProps = {
  quote: string
  align?: 'top' | 'bottom'
}

const LargeQuote = ({ quote, align }: LargeQuoteProps) => {
  const root = useRef<HTMLDivElement>(null!)
  const { lenis } = useLenis()

  useGSAP(
    () => {
      if (!lenis) return

      const matchMedia = gsap.matchMedia(root.current)

      matchMedia.add(MediaQuery.LARGE, () => {
        gsap
          .timeline({
            scrollTrigger: {
              trigger: root.current,
              start: 'left 100%',
              horizontal: true,
              markers: false,
              scrub: true
            }
          })
          .fromTo(root.current, { xPercent: 0 }, { xPercent: 40, ease: 'none' })
      })
    },
    { scope: root, dependencies: [lenis] }
  )

  return (
    <Section
      ref={root}
      className={cn('pointer-events-none', {
        'mb-spacing-lg lg:z-10 lg:-mr-[5.8rem] lg:mb-0': align === 'bottom'
      })}
    >
      <Motion animation={largeQuoteRevealAnimation}>
        {(motionRef) => (
          <Grid ref={motionRef}>
            <div
              className={cn([
                'col-span-2 md:col-span-4 md:col-start-2',
                'lg:col-auto lg:row-span-10 lg:w-col-span-4',
                'grid',
                {
                  'items-start lg:row-start-4': align === 'top',
                  'items-end': align === 'bottom'
                }
              ])}
            >
              <Typography
                variant="h2"
                paragraph
                className="capitalize"
                data-motion-quote
              >
                {quote}
              </Typography>
            </div>
          </Grid>
        )}
      </Motion>
    </Section>
  )
}

export default LargeQuote
